import React, { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LabelWithInput from 'components/common/Input/LabelWithInput';
import 'assets/styles/common.scss';
import CheckValidation from 'utils/Validation';
import ToastNotification from 'components/common/Toast';
import { LocaleContext } from 'routes/Router';
import { superAdminDashboard, login } from 'routes/AppUrls';
import { FormattedMessage, useIntl } from 'react-intl';
import ReusableButton from 'components/common/Button';
import PhoneNumberInput from 'components/common/Input/PhoneNumberInput';
import ReusableTabs from 'components/common/Tabs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { registerInviteBusinessCallAPI } from 'store/ProviderAdmin/registerInviteBusiness/action';
import { useDispatch, useSelector } from 'react-redux';
import AuthLayout from 'components/common/AuthLayout';
import Ptext from 'components/common/Ptext';
import signupHero from 'assets/images/login/signupHero.jpg';
import LoginLogo from 'assets/images/login/LogoAlt.svg';
import 'assets/styles/auth.scss';

const GetStarted = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { locale } = useContext(LocaleContext);
  const [conf, setConf] = ToastNotification();
  const [isError, setIsError] = useState(false);
  const phoneNumberInputRef = useRef(null);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [tab, setTab] = useState(1);

  const inviteSendLoader = useSelector(
    (state) => state?.registerInviteBusinessReducer?.loading
  );

  const [formValue, setformValue] = useState({
    name: '',
    email: '',
    countryCode: '+974',
    phoneNumber: '',
  });

  const handleSubmit = () => {
    const { name, email, phoneNumber } = formValue;
    const error = CheckValidation({ name, email, phoneNumber });
    setIsError(error);
    if (error.submit && isPhoneNumberValid) {
      let body = {
        business_type:
          tab === 1 ? 'specialist' : tab === 2 ? 'salon' : 'company',
        name_en: formValue?.name,
        name_ar: formValue?.name,
        contact_number: formValue.phoneNumber,
        email: formValue.email?.toLowerCase(),
        country_code: formValue?.countryCode,
      };
      dispatch(
        registerInviteBusinessCallAPI(body, {
          run: true,
          toast: true,
          handleFunction: (res) => handleRedirect(res),
          handleToastError: (res) => handleError(res),
          navigate: navigate,
        })
      );
    }
  };

  const handleRedirect = (res) => {
    setConf({
      msg: res?.data?.message,
      variant: 'success',
    });
    setTimeout(() => {
      navigate(superAdminDashboard(locale));
    }, 1000);
  };

  const handleError = (res) => {
    setConf({
      msg: res?.data?.message,
      variant: 'error',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setformValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCountryCodeChange = (value) => {
    setformValue({ ...formValue, countryCode: `+${value}` });
  };

  const handlePhoneNumberChange = (e) => {
    const phoneNumberValue = e.target.value.replace(/\D/g, '');
    setformValue((prev) => ({
      ...prev,
      [e.target.name]: phoneNumberValue,
    }));

    const fullPhoneNumber = `${formValue?.countryCode}${phoneNumberValue}`;
    const parsedPhoneNumber = parsePhoneNumberFromString(fullPhoneNumber);

    if (parsedPhoneNumber) {
      setIsPhoneNumberValid(parsedPhoneNumber.isValid());
    } else {
      setIsPhoneNumberValid(false);
    }
  };

  const tabData = [
    { label: intl.formatMessage({ id: 'Specialist' }) },
    { label: intl.formatMessage({ id: 'Salon' }) },
    { label: intl.formatMessage({ id: 'Company' }) },
  ];

  const handleTabChange = (val) => {
    setTab(val);
  };

  return (
    <AuthLayout
      heroImage={signupHero}
      zoomHero={true}
      logo={LoginLogo}
      quote="Allue transformed our salon by doubling our monthly bookings and completely eliminating no-shows"
      author="Alisa Lisa"
    >
      <div className="login-content">
        <div className="text-center">
          <Ptext variant="title">
            <FormattedMessage id="Get Started" />
          </Ptext>
        </div>

        <Ptext variant="body" className="text-center mt-4">
          <FormattedMessage id="Already have an account?" />{' '}
          <button onClick={() => navigate(login(locale))}>
            <span className="font-medium signup-link">
              <FormattedMessage id="Login" />
            </span>
          </button>
        </Ptext>

        <div className="mt-3">
          <LabelWithInput
            label={<FormattedMessage id="Name" />}
            placeholder={intl.formatMessage({ id: 'Name' })}
            name="name"
            type="text"
            value={formValue?.name}
            onChange={handleInputChange}
            errorText={
              isError && isError.name.error && isError.name.errorMessage
            }
          />
        </div>

        <div className="mt-3">
          <LabelWithInput
            label={<FormattedMessage id="E-mail Address" />}
            placeholder={intl.formatMessage({ id: 'E-mail Address' })}
            name="email"
            type="text"
            value={formValue?.email}
            onChange={handleInputChange}
            errorText={
              isError && isError.email.error && isError.email.errorMessage
            }
          />
        </div>

        <div className="mt-2">
          <PhoneNumberInput
            label={<FormattedMessage id="Contact Number" />}
            country={'qa'}
            value={formValue?.countryCode}
            name={'phoneNumber'}
            labelFontFamily="Poppins, sans-serif"
            onCountryCodeChange={handleCountryCodeChange}
            onPhoneNumberChange={handlePhoneNumberChange}
            phoneNumber={formValue.phoneNumber}
            phoneNumberRef={phoneNumberInputRef}
            isPhoneNumberValid={isPhoneNumberValid}
            isError={
              isError &&
              isError.phoneNumber.error &&
              isError.phoneNumber.errorMessage
            }
            errorMessage="Invalid phone number for selected country code"
          />
        </div>

        <div className="mt-3 pt-2">
          <div style={{ paddingBottom: '5px' }}>
            <Ptext variant="body" className="font-medium">
              <FormattedMessage id="Business Type" />
            </Ptext>
          </div>

          <ReusableTabs
            tabs={tabData}
            defaultTab={1}
            currentTab={(val) => handleTabChange(val)}
            hasBorder={'#DDDDDD'}
            borderRadius="12px"
          />
        </div>

        <div className="mt-4 pt-1">
          <ReusableButton
            fullWidth={true}
            variant="contained"
            color="#FFFFFF"
            fontSize="16px"
            fontWeight="500"
            padding="14px"
            bgColor="#000000"
            borderRadius="999px"
            fontFamily="Poppins, sans-serif"
            loading={inviteSendLoader}
            onClick={() => handleSubmit()}
          >
            <FormattedMessage id="Submit" />
          </ReusableButton>
        </div>
      </div>
    </AuthLayout>
  );
};

export default GetStarted;
